import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle, XCircle, Printer, Download, ArrowLeft, Loader2 } from 'lucide-react';
import { getApiBase } from '../lib/apiBase';

const ReceiptPage = ({ orderId, gateway, failed, onClose }) => {
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const receiptRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let timer;
    let controller;
    let attempts = 0;
    const load = async () => {
      if (!orderId) { setError('Payment reference unavailable. Contact SAG before paying again.'); setLoading(false); return; }
      controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 25000);
      try {
        const response = await fetch(`${getApiBase()}/api/receipts/${encodeURIComponent(orderId)}`, { signal: controller.signal });
        if (!response.ok) throw new Error('Payment confirmation is temporarily unavailable. Please keep your reference and do not pay again.');
        const data = await response.json();
        if (cancelled) return;
        setReceipt(data); setError(''); setLoading(false);
        if (gateway === 'stripe' && data.payment_status !== 'paid' && data.status !== 'expired' && attempts++ < 8) timer = setTimeout(load, 3000);
      } catch (err) {
        if (cancelled) return;
        setError(err.message); setLoading(false);
        if (gateway === 'stripe' && attempts++ < 3) timer = setTimeout(load, 3000);
      } finally { clearTimeout(timeout); }
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); controller?.abort(); };
  }, [orderId, gateway]);

  const handlePrint = () => window.print();

  const handleDownload = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;
      const el = receiptRef.current;
      if (!el) return;
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff' });
      const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const ratio = pageWidth / canvas.width;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 20, pageWidth, canvas.height * ratio);
      pdf.save(`receipt-${orderId}.pdf`);
    } catch {
      alert('PDF generation failed. Please use Print instead.');
    }
  };

  const isSuccess = !failed && receipt && (receipt.payment_status === 'paid' || receipt.status === 'APPROVED');
  const amount = receipt?.amount || receipt?.chargetotal || '0.00';
  const curr = receipt?.currency || 'USD';

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
        <div className="bg-white p-10 text-center">
          <Loader2 className="w-8 h-8 text-[#C9A227] animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading receipt...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4 overflow-y-auto py-8">
      <div className="bg-white w-full max-w-lg shadow-2xl relative" data-testid="receipt-modal">
        {/* Actions bar */}
        <div className="bg-[#0a0a0a] px-5 py-3 flex items-center justify-between print:hidden">
          <span className="text-white/60 text-xs tracking-widest uppercase">Payment Receipt</span>
          <div className="flex items-center gap-3">
            <button onClick={handlePrint} className="text-white/40 hover:text-[#C9A227] transition-colors" title="Print">
              <Printer className="w-4 h-4" />
            </button>
            <button onClick={handleDownload} className="text-white/40 hover:text-[#C9A227] transition-colors" title="Download PDF">
              <Download className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="text-white/40 hover:text-white transition-colors text-lg">&times;</button>
          </div>
        </div>

        {/* Receipt content */}
        <div ref={receiptRef} className="p-8">
          {/* Logo */}
          <div className="text-center mb-6">
            <img src="/logos/sag-logo.png" alt="SAG" className="h-10 mx-auto mb-3" />
            <p className="text-[10px] text-slate-400 tracking-widest uppercase">Sebastian Alliance Group</p>
          </div>

          {/* Status */}
          <div className="text-center mb-6">
            {isSuccess ? (
              <>
                <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-7 h-7 text-green-600" />
                </div>
                <h2 className="text-xl font-medium text-green-800">Payment Successful</h2>
              </>
            ) : (
              <>
                <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <XCircle className="w-7 h-7 text-red-600" />
                </div>
                <h2 className="text-xl font-medium text-red-800">{failed || receipt?.payment_status === 'failed' || receipt?.status === 'expired' ? 'Payment not completed' : 'Awaiting payment confirmation'}</h2>
                <p className="text-sm mt-2">{error || 'We are checking the payment. Do not pay again while confirmation is pending.'}</p>
                <p className="text-xs mt-2 break-all">Reference: {orderId}</p>
                {receipt?.fail_reason && <p className="text-red-500 text-sm mt-1">{receipt.fail_reason}</p>}
              </>
            )}
          </div>

          {isSuccess && (
            <>
              {/* Amount */}
              <div className="text-center mb-6 pb-6 border-b border-slate-100">
                <p className="text-4xl font-light text-[#0a0a0a]">
                  {curr} {typeof amount === 'number' ? amount.toFixed(2) : amount}
                </p>
              </div>

              {/* Details */}
              <div className="space-y-3 mb-6">
                {receipt?.receipt_number && <p className="text-sm">Receipt: {receipt.receipt_number}</p>}
                {receipt?.order_id && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Order ID</span>
                    <span className="text-slate-700 font-medium">{receipt.order_id}</span>
                  </div>
                )}
                {receipt?.invoice_number && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Invoice</span>
                    <span className="text-slate-700 font-medium">{receipt.invoice_number}</span>
                  </div>
                )}
                {receipt?.transaction_id && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Transaction ID</span>
                    <span className="text-slate-700 font-medium font-mono text-xs">{receipt.transaction_id}</span>
                  </div>
                )}
                {receipt?.approval_code && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Approval Code</span>
                    <span className="text-slate-700 font-medium">{receipt.approval_code}</span>
                  </div>
                )}
                {receipt?.ref_number && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Reference</span>
                    <span className="text-slate-700 font-medium">{receipt.ref_number}</span>
                  </div>
                )}
                {(receipt?.card_brand || receipt?.card_last4) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Payment Method</span>
                    <span className="text-slate-700 font-medium">
                      {receipt.card_brand} {receipt.card_last4 ? `****${receipt.card_last4}` : ''}
                    </span>
                  </div>
                )}
                {receipt?.customer_name && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Customer</span>
                    <span className="text-slate-700 font-medium">{receipt.customer_name}</span>
                  </div>
                )}
                {receipt?.customer_email && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Email</span>
                    <span className="text-slate-700 font-medium">{receipt.customer_email}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Gateway</span>
                  <span className="text-slate-700 font-medium">{gateway === 'fiserv' ? 'Scotiabank (Fiserv IPG)' : 'Stripe'}</span>
                </div>
                {(receipt?.timestamp || receipt?.created_at) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Date</span>
                    <span className="text-slate-700 font-medium">{receipt.timestamp || new Date(receipt.paid_at || receipt.created_at).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Receipt email notice */}
              <div className="bg-slate-50 border border-slate-100 p-4 text-center mb-4">
                <p className="text-xs text-slate-500">
                  {receipt?.email_status === 'sent'
                    ? `Receipt email submitted successfully to ${receipt.customer_email}.`
                    : receipt?.email_status === 'queued'
                      ? 'Your receipt email is queued for delivery.'
                      : 'Email delivery is not confirmed. Save or print this receipt for your records.'}

                </p>
              </div>
            </>
          )}

          {isSuccess && receipt?.receipt_url && <a className="block text-center underline mb-4" href={receipt.receipt_url} target="_blank" rel="noopener noreferrer">View Stripe receipt</a>}
          {/* Footer */}
          <div className="text-center pt-4 border-t border-slate-100">
            <p className="text-[10px] text-slate-300 tracking-wider uppercase">
              Sebastian Alliance Group &bull; Fort Lauderdale &bull; Nassau &bull; London
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-8 pb-6 flex gap-3 print:hidden">
          <button
            onClick={handlePrint}
            disabled={!isSuccess}
            className="flex-1 py-3 border border-slate-200 text-slate-600 text-xs tracking-wider uppercase font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
          >
            <Printer className="w-3.5 h-3.5" /> Print Receipt
          </button>
          <button
            onClick={handleDownload}
            disabled={!isSuccess}
            className="flex-1 py-3 bg-[#0a0a0a] text-white text-xs tracking-wider uppercase font-medium hover:bg-[#C9A227] hover:text-[#0a0a0a] transition-colors flex items-center justify-center gap-2"
          >
            <Download className="w-3.5 h-3.5" /> Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptPage;
